/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */
// deps
import { useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { AnalyticsContext, DELIVERY_SECTION_ELEMENT } from "@swiggy-private/checkout-core";
import {
    DELIVERY_SPEED,
    DELIVERY_SPEED_STRATEGY,
    DeliverySpeedDetail,
    Option,
    ReviewedCart,
} from "@swiggy-private/cart-types";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import { captureError, useEffectOnce } from "@swiggy-private/core-ui";

// helpers
import {
    DeliverySpeedAppStorage,
    canShowDeliverySpeedCrouton as canShowDeliverySpeedCroutonHelper,
    checkServiceability,
    getDeliverySpeedStorageValues,
    getDeliverySpeedWidgetInfoV2,
    getSelectedSlaFromSlaOptions,
    resetCroutonCountStorageValue,
    setDeliverySpeedStorageValues,
} from "@cart-client/helpers/CartDeliverySpeed";
import { isUserLoggedIn } from "@cart-client/helpers/User";

// analytics
import { clickCartNoRushNudge, clickDeliverySpeedItem, impressionCartNoRushNudge } from "@cart-client/analytics/events";

// constants
import { APP_STORAGE_KEYS_FOR_DELIVERY_SPEED } from "@cart-client/constants/CartDeliverySpeed";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";

export interface UseCartDeliverySpeedReturnType {
    hideDeliverySpeedCrouton: VoidFunction;
    onDeliverySpeedItemClick: (item: DeliverySpeedDetail, fromHalfCard?: boolean, text?: string) => void;
    deliverySpeedCroutonText?: string;
    deliverySpeedCroutonSubText?: string;
    deliverySpeedCroutonIcon?: string;
    selectedSlaOption: DeliverySpeedDetail | undefined;
    onCroutonClick: VoidFunction;
    onCroutonLoad: VoidFunction;
    onOptInWidgetClick: (optInValue: boolean) => void;
    deliverySpeedWidgetInfoV2: {
        options: Option[];
        optInText?: string;
        slaComms?: { slaText?: string; slaLogo?: string };
    } | null;
    onOfferTimerEnds?: VoidFunction;
}

export interface UseCartDeliverySpeedProps {
    cartResponse: ReviewedCart | null;
    canShowDeliverySpeedCrouton: boolean;
    setDeliverySpeedCroutonActive: (isDeliverySpeedCroutonActive: boolean) => void;
}

export function useCartDeliverySpeed({
    cartResponse,
    canShowDeliverySpeedCrouton,
    setDeliverySpeedCroutonActive,
}: UseCartDeliverySpeedProps): UseCartDeliverySpeedReturnType {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { localCart } = cartState;
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { setDeliveryTab } = useContext(DeliverySectionContext);
    const { selectedAddress, cart_id: cartId, restaurantId } = localCart || {};
    const { sla_options: slaOptions, selected_sla_option: selectedSlaValue, id: addressId } = selectedAddress || {};
    const [deliverySpeedStorageData, setDeliverySpeedStorageData] = useState<DeliverySpeedAppStorage>({
        [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID]: "",
        [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT]: 0,
    });
    const userLoggedIn = isUserLoggedIn();

    const triggerUpdateCart = useCallback(
        async (slaOption: DELIVERY_SPEED) => {
            try {
                if (!updateCartInProgress)
                    await doUpdateCart({
                        ...localCart,
                        selected_sla_option: slaOption,
                        customer_selected_sla_option: slaOption,
                    });
            } catch (err) {
                captureError(err);
            }
        },
        [updateCartInProgress, doUpdateCart, localCart],
    );

    const onDeliverySpeedItemClick = useCallback(
        (item: DeliverySpeedDetail) => {
            clickDeliverySpeedItem({ type: item?.type, clickEvent, cartId, addressId, restaurantId });
            if (item?.strategy?.toUpperCase() === DELIVERY_SPEED_STRATEGY.VISIBLE) {
                triggerUpdateCart(item.type);
            }
        },
        [addressId, cartId, clickEvent, triggerUpdateCart, restaurantId],
    );

    const onOptInWidgetClick = useCallback(
        async (optInValue: boolean) => {
            try {
                if (!updateCartInProgress) {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    await doUpdateCart({ ...localCart, delivery_type_opt_in: optInValue });

                    if (!optInValue) {
                        resetCroutonCountStorageValue();
                    }
                }
            } catch (err) {
                captureError(err);
            }
        },
        [doUpdateCart, localCart, updateCartInProgress],
    );

    // to check whether rx is unservicable
    const isRxUnservicable = useMemo(
        () =>
            checkServiceability(
                cartResponse?.statusCode,
                { userLoggedIn, selectedAddress },
                CART_STATUS_CODES.RESTAURANT_UNSERVICEABLE,
                0,
            ),
        [cartResponse?.statusCode, selectedAddress, userLoggedIn],
    );
    // to check whether location is unservicable
    const isLocationServicable = useMemo(
        () =>
            checkServiceability(
                cartResponse?.statusCode,
                { userLoggedIn, selectedAddress },
                CART_STATUS_CODES.SUCCESS,
                1,
            ),
        [cartResponse?.statusCode, selectedAddress, userLoggedIn],
    );

    const isAddressServiceable = !!isLocationServicable;
    const isRestaurantUnservicable = !!isRxUnservicable;

    const deliverySpeedWidgetInfoV2 = useMemo(
        () =>
            getDeliverySpeedWidgetInfoV2(
                cartResponse?.data,
                localCart,
                isAddressServiceable,
                isRestaurantUnservicable,
                updateCartInProgress,
            ),
        [cartResponse?.data, localCart, isAddressServiceable, isRestaurantUnservicable, updateCartInProgress],
    );

    const canShowCrouton = useCallback(
        (storageData: DeliverySpeedAppStorage) =>
            canShowDeliverySpeedCroutonHelper({
                storageData,
                showCrouton: canShowDeliverySpeedCrouton,
                slaOptions,
            }),
        [canShowDeliverySpeedCrouton, slaOptions],
    );

    const checkCroutonVisibility = useCallback(
        (storageData: DeliverySpeedAppStorage) => {
            if (!canShowCrouton(storageData)) {
                return;
            }
            setDeliverySpeedCroutonActive(true);
        },
        [canShowCrouton, setDeliverySpeedCroutonActive],
    );

    const getDeliverySpeedDataFromAppStorage = useCallback(async () => {
        const deliverySpeedAppStorageData = await getDeliverySpeedStorageValues();

        if (deliverySpeedAppStorageData != null) {
            setDeliverySpeedStorageData(deliverySpeedAppStorageData);
            checkCroutonVisibility(deliverySpeedAppStorageData);
        }
    }, [checkCroutonVisibility]);

    const selectedSlaOption = useMemo(
        () => getSelectedSlaFromSlaOptions(slaOptions, selectedSlaValue),
        [selectedSlaValue, slaOptions],
    );

    const hideDeliverySpeedCrouton = useCallback(() => {
        setDeliverySpeedCroutonActive(false);
    }, [setDeliverySpeedCroutonActive]);

    const updateDeliverySpeedStorageValue = useCallback(
        (key: APP_STORAGE_KEYS_FOR_DELIVERY_SPEED) => {
            setDeliverySpeedStorageValues(key, deliverySpeedStorageData);
        },
        [deliverySpeedStorageData],
    );

    const onCroutonLoad = useCallback(() => {
        impressionCartNoRushNudge(impressionEvent, cartId, addressId);
        updateDeliverySpeedStorageValue(APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT);
    }, [addressId, cartId, impressionEvent, updateDeliverySpeedStorageValue]);

    const deliverySpeedCroutonText = useMemo(() => {
        return cartResponse?.data?.delivery_speed_crouton?.message;
    }, [cartResponse?.data?.delivery_speed_crouton?.message]);

    const deliverySpeedCroutonSubText = cartResponse?.data?.delivery_speed_crouton?.subtext;

    const deliverySpeedCroutonIcon = cartResponse?.data?.delivery_speed_crouton?.icon;

    const onCroutonClick = useCallback(() => {
        setDeliveryTab(DELIVERY_SECTION_ELEMENT.DELIVERY_TYPE);

        clickCartNoRushNudge(clickEvent, cartId, addressId);

        hideDeliverySpeedCrouton();
    }, [addressId, cartId, clickEvent, hideDeliverySpeedCrouton, setDeliveryTab]);

    const onOfferTimerEnds = useCallback(async () => {
        try {
            await doUpdateCart({
                ...localCart,
            });
        } catch (err) {
            captureError(err);
        }
    }, [doUpdateCart, localCart]);

    useEffectOnce(() => {
        getDeliverySpeedDataFromAppStorage();
    });

    return {
        hideDeliverySpeedCrouton,
        onDeliverySpeedItemClick,
        deliverySpeedWidgetInfoV2,
        selectedSlaOption,
        deliverySpeedCroutonText,
        deliverySpeedCroutonSubText,
        deliverySpeedCroutonIcon,
        onCroutonClick,
        onCroutonLoad,
        onOptInWidgetClick,
        onOfferTimerEnds,
    };
}
